import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Users, ShieldCheck, Globe2, BadgeCheck, Zap, FileText, Globe, Cog, Sparkles,
  Megaphone, LayoutTemplate, Smartphone, Briefcase, Scale, HeartPulse, Cpu,
  ShoppingBag, Landmark, Factory, Plane, ArrowRight, ArrowUpRight, Quote,
  UploadCloud, FileSearch, PenLine, SearchCheck, PackageCheck, Clock,
} from "lucide-react";
import { api } from "@/lib/api";

const reveal = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.08, duration: 0.7, ease: [0.16, 1, 0.3, 1] },
  }),
};

function Overline({ children }) {
  return <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C87A4B] mb-4">{children}</p>;
}

export function LanguageMarquee() {
  const pairs = [
    "English → Arabic", "English → French", "English → German", "English → Spanish",
    "English → Japanese", "French → German", "English → Chinese", "German → English",
    "English → Portuguese", "Spanish → English", "English → Korean", "Italian → English",
  ];
  const row = [...pairs, ...pairs];
  return (
    <div id="languages" data-testid="language-marquee" className="border-y border-slate-200 bg-white py-5 overflow-hidden">
      <div className="flex whitespace-nowrap animate-marquee w-max">
        {row.map((p, i) => (
          <span key={i} className="font-heading text-lg sm:text-xl font-semibold text-slate-300 mx-6 flex items-center gap-6">
            {p} <span className="text-[#C87A4B] text-sm">◆</span>
          </span>
        ))}
      </div>
    </div>
  );
}

export function ComplexCta() {
  return (
    <section data-testid="complex-cta" className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16">
      <motion.div
        variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}
        className="rounded-2xl bg-[#0B111E] text-white px-8 py-10 sm:px-12 flex flex-col lg:flex-row lg:items-center gap-8 justify-between"
      >
        <div>
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C87A4B] mb-3">Beyond the standard</p>
          <h3 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-2">Have a more complex project?</h3>
          <p className="text-slate-400 text-sm sm:text-base max-w-xl leading-relaxed">
            Our team can prepare tailored solutions for large documents, websites, software, regulatory
            documentation, multilingual projects and ongoing translation requirements.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 shrink-0">
          <a href="/contact" data-testid="complex-talk-btn"
            className="inline-flex items-center justify-center gap-2 bg-white text-[#0B111E] font-semibold text-sm px-6 py-3.5 rounded-lg hover:bg-slate-100 transition-colors">
            Talk to Our Team <ArrowUpRight className="w-4 h-4" />
          </a>
          <a href="/#quote" data-testid="complex-custom-quote-btn"
            className="inline-flex items-center justify-center gap-2 bg-[#C87A4B] text-white font-semibold text-sm px-6 py-3.5 rounded-lg hover:bg-[#B3673A] transition-colors">
            Request a Custom Quote <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </motion.div>
    </section>
  );
}

const TRUST = [
  { icon: Users, label: "Professional Linguists" },
  { icon: ShieldCheck, label: "Secure & Confidential" },
  { icon: Globe2, label: "Global Language Coverage" },
  { icon: BadgeCheck, label: "Quality Reviewed" },
  { icon: Clock, label: "Fast Turnaround" },
];

export function TrustStrip() {
  return (
    <section data-testid="trust-strip" className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-14">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6">
        {TRUST.map((t, i) => (
          <motion.div
            key={t.label}
            variants={reveal} initial="hidden" whileInView="visible" custom={i}
            viewport={{ once: true, margin: "-60px" }}
            className="flex flex-col items-center text-center gap-3"
          >
            <span className="w-12 h-12 rounded-xl bg-white border border-slate-200 shadow-sm flex items-center justify-center text-[#C87A4B]">
              <t.icon className="w-5 h-5" strokeWidth={1.7} />
            </span>
            <span className="text-sm font-semibold text-[#0B111E]">{t.label}</span>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

const WHY = [
  { n: "01", title: "Professional Quality", text: "Translations completed and reviewed using structured quality assurance processes." },
  { n: "02", title: "Fast Turnaround", text: "Flexible delivery options for standard and time-sensitive projects." },
  { n: "03", title: "Secure & Confidential", text: "Customer documents are handled securely and confidentially throughout the translation process." },
  { n: "04", title: "Global Coverage", text: "Professional translation support across international languages and markets." },
];

export function WhyUs() {
  return (
    <section id="why-us" data-testid="why-us" className="bg-white border-y border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-20 lg:py-28">
        <motion.div variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}>
          <Overline>Why choose us</Overline>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight text-[#0B111E] max-w-2xl">
            Precision in every language, care in every project.
          </h2>
        </motion.div>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-12">
          {WHY.map((w, i) => (
            <motion.div
              key={w.n}
              variants={reveal} initial="hidden" whileInView="visible" custom={i}
              viewport={{ once: true, margin: "-60px" }}
              className="flex gap-6 group"
            >
              <span className="font-mono2 text-sm text-[#C87A4B] pt-1.5 shrink-0">{w.n}</span>
              <div className="border-l border-slate-200 pl-6 group-hover:border-[#C87A4B] transition-colors duration-300">
                <h3 className="font-heading text-xl sm:text-2xl font-semibold tracking-tight text-[#0B111E] mb-2">{w.title}</h3>
                <p className="text-base leading-relaxed text-slate-600">{w.text}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

const SERVICES = [
  { icon: FileText, title: "Document Translation", desc: "Accurate translation of business and personal documents in any format." },
  { icon: BadgeCheck, title: "Certified Translation", desc: "Certified translations for official, legal and regulatory use." },
  { icon: Globe, title: "Website Localisation", desc: "Full website localisation that preserves tone, structure and SEO." },
  { icon: Cog, title: "Technical & Regulatory", desc: "Specialist translation for technical, compliance and regulatory content." },
  { icon: Sparkles, title: "Cosmetics & Beauty", desc: "Product information, labelling and compliance documentation for beauty brands." },
  { icon: Megaphone, title: "Marketing Translation", desc: "Brand voice adapted persuasively for each target market." },
  { icon: LayoutTemplate, title: "Multilingual DTP", desc: "Print-ready layout and typesetting in every target language." },
  { icon: Smartphone, title: "Software & App Localisation", desc: "Interface strings, app store content and UX copy, localised." },
  { icon: Zap, title: "Urgent Translation", desc: "Priority turnaround for time-sensitive documents and launches." },
  { icon: Briefcase, title: "Business Translation", desc: "Reports, contracts and corporate communications, handled professionally." },
];

export function Services() {
  return (
    <section id="services" data-testid="services-section" className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-20 lg:py-28">
      <motion.div variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
        <div>
          <Overline>Solutions</Overline>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0B111E]">Translation services</h2>
        </div>
        <p className="text-slate-500 text-sm max-w-xs">Every project is scoped individually — no fixed price lists, no surprises.</p>
      </motion.div>
      <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {SERVICES.map((s, i) => (
          <motion.div
            key={s.title}
            variants={reveal} initial="hidden" whileInView="visible" custom={i % 3}
            viewport={{ once: true, margin: "-40px" }}
            data-testid={`service-card-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className="group bg-white rounded-2xl border border-slate-200 p-7 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(11,17,30,0.08)] hover:border-[#C87A4B]/50 transition-all duration-300"
          >
            <span className="w-11 h-11 rounded-lg bg-[#FAF8F5] border border-slate-200 flex items-center justify-center text-[#C87A4B] mb-5 group-hover:bg-[#C87A4B] group-hover:text-white group-hover:border-[#C87A4B] transition-all duration-300">
              <s.icon className="w-5 h-5" strokeWidth={1.7} />
            </span>
            <h3 className="font-heading text-lg font-semibold text-[#0B111E] mb-1.5">{s.title}</h3>
            <p className="text-sm text-slate-500 leading-relaxed mb-5">{s.desc}</p>
            <div className="flex items-center gap-5 text-sm font-semibold">
              <a href="/contact" className="text-slate-600 hover:text-[#0B111E] transition-colors">Learn More</a>
              <a href="/#quote" className="text-[#C87A4B] hover:text-[#B3673A] inline-flex items-center gap-1 transition-colors">
                Get a Quote <ArrowRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

const INDUSTRIES = [
  { icon: Sparkles, name: "Cosmetics & Beauty", note: "Regulatory, INCI & product documentation" },
  { icon: Scale, name: "Legal", note: "Contracts, patents & sworn translation" },
  { icon: HeartPulse, name: "Healthcare", note: "Clinical, medical & life sciences" },
  { icon: Cpu, name: "Technology", note: "SaaS, software & documentation" },
  { icon: ShoppingBag, name: "Ecommerce", note: "Catalogues, listings & storefronts" },
  { icon: Landmark, name: "Financial Services", note: "Reports, filings & disclosures" },
  { icon: Factory, name: "Manufacturing", note: "Manuals, specs & safety data" },
  { icon: Megaphone, name: "Marketing", note: "Campaigns & brand content" },
  { icon: Plane, name: "Travel & Hospitality", note: "Guest experience & bookings" },
  { icon: Briefcase, name: "General Business", note: "Day-to-day corporate needs" },
];

export function Industries() {
  return (
    <section id="industries" data-testid="industries-section" className="bg-[#0B111E] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-20 lg:py-28">
        <motion.div variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}>
          <Overline>Industries</Overline>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight max-w-2xl">
            Specialist knowledge, sector by sector.
          </h2>
          <p className="text-slate-400 mt-4 max-w-xl text-base leading-relaxed">
            From cosmetics compliance to legal documentation, your content is handled by linguists who understand the field.
          </p>
        </motion.div>
        <div className="mt-12 grid grid-cols-2 lg:grid-cols-5 gap-px bg-slate-800 rounded-2xl overflow-hidden border border-slate-800">
          {INDUSTRIES.map((ind, i) => (
            <motion.div
              key={ind.name}
              variants={reveal} initial="hidden" whileInView="visible" custom={i % 5}
              viewport={{ once: true, margin: "-40px" }}
              data-testid={`industry-${ind.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="bg-[#0B111E] p-6 hover:bg-[#111a2c] transition-colors duration-300 group"
            >
              <ind.icon className="w-5 h-5 text-[#C87A4B] mb-4 group-hover:scale-110 transition-transform duration-300" strokeWidth={1.7} />
              <h3 className="font-heading font-semibold text-sm sm:text-base mb-1">{ind.name}</h3>
              <p className="text-xs text-slate-500 leading-relaxed">{ind.note}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

const STEPS = [
  { icon: UploadCloud, n: "1", title: "Upload", text: "Upload your documents and select your languages." },
  { icon: FileSearch, n: "2", title: "Receive Quote", text: "Receive a clear quotation and expected delivery date." },
  { icon: PenLine, n: "3", title: "Translation", text: "Your project is assigned and translated." },
  { icon: SearchCheck, n: "4", title: "Quality Review", text: "The completed translation undergoes quality review." },
  { icon: PackageCheck, n: "5", title: "Delivery", text: "Receive your completed translation securely." },
];

export function HowItWorks() {
  return (
    <section id="how-it-works" data-testid="how-it-works" className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-20 lg:py-28">
      <motion.div variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} className="text-center">
        <Overline>How it works</Overline>
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0B111E]">
          Five steps. Zero friction.
        </h2>
      </motion.div>
      <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-6">
        {STEPS.map((s, i) => (
          <motion.div
            key={s.n}
            variants={reveal} initial="hidden" whileInView="visible" custom={i}
            viewport={{ once: true, margin: "-40px" }}
            data-testid={`step-${s.n}`}
            className="relative bg-white rounded-2xl border border-slate-200 p-6 text-center hover:border-[#C87A4B]/50 transition-colors duration-300"
          >
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 w-7 h-7 rounded-full bg-[#0B111E] text-white text-xs font-mono flex items-center justify-center">{s.n}</span>
            <s.icon className="w-7 h-7 mx-auto text-[#C87A4B] mt-2 mb-4" strokeWidth={1.6} />
            <h3 className="font-heading font-semibold text-[#0B111E] mb-1.5">{s.title}</h3>
            <p className="text-xs text-slate-500 leading-relaxed">{s.text}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

export function ClientLogos() {
  const [logos, setLogos] = useState(null);
  useEffect(() => { api.get("/logos").then((r) => setLogos(r.data)).catch(() => setLogos([])); }, []);
  return (
    <section data-testid="client-logos" className="bg-white border-y border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16">
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-400 text-center mb-10">Trusted by</p>
        {logos && logos.length > 0 ? (
          <div className="flex flex-wrap items-center justify-center gap-x-14 gap-y-8">
            {logos.map((l) => (
              <img
                key={l.id}
                src={`${process.env.REACT_APP_BACKEND_URL}/api/logos/${l.id}/image`}
                alt={`${l.name} logo`}
                data-testid={`client-logo-${l.id}`}
                className="h-9 object-contain grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all duration-300"
                loading="lazy"
              />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-6 items-center">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                data-testid={`client-logo-placeholder-${i}`}
                className="h-12 rounded-lg border border-dashed border-slate-200 flex items-center justify-center"
              >
                <span className="text-[10px] font-mono uppercase tracking-widest text-slate-300">Client Logo</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export function Testimonials() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/testimonials").then((r) => setItems(r.data)).catch(() => {}); }, []);
  if (!items.length) return null;
  return (
    <section data-testid="testimonials" className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-20 lg:py-28">
      <motion.div variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}>
        <Overline>Testimonials</Overline>
        <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-[#0B111E]">What our clients say</h2>
      </motion.div>
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
        {items.map((t, i) => (
          <motion.figure
            key={t.id}
            variants={reveal} initial="hidden" whileInView="visible" custom={i}
            viewport={{ once: true, margin: "-40px" }}
            data-testid={`testimonial-${t.id}`}
            className="bg-white rounded-2xl border border-slate-200 p-8"
          >
            <Quote className="w-6 h-6 text-[#C87A4B] mb-4" strokeWidth={1.6} />
            <blockquote className="text-base text-slate-700 leading-relaxed mb-6">{t.quote}</blockquote>
            <figcaption className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-sm text-[#0B111E]">{t.customer_name}</p>
                <p className="text-xs text-slate-500">{[t.position, t.company].filter(Boolean).join(" · ")}</p>
              </div>
              {t.is_placeholder && (
                <span className="text-[10px] font-mono uppercase tracking-widest text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1">
                  Placeholder
                </span>
              )}
            </figcaption>
          </motion.figure>
        ))}
      </div>
    </section>
  );
}

export function FinalCta() {
  return (
    <section data-testid="final-cta" className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pb-20 lg:pb-28">
      <motion.div
        variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}
        className="relative rounded-3xl bg-[#0B111E] overflow-hidden px-8 py-16 sm:px-16 text-center"
      >
        <div className="absolute inset-0 hero-grid-bg opacity-20" style={{ backgroundImage: "linear-gradient(to right, rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.06) 1px, transparent 1px)" }} />
        <div className="relative">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-4">Ready to translate?</h2>
          <p className="text-slate-400 text-base mb-8">Upload your documents and receive your quotation.</p>
          <a
            href="#quote"
            data-testid="final-cta-btn"
            className="inline-flex items-center gap-2 bg-[#C87A4B] hover:bg-[#B3673A] text-white font-heading font-bold text-lg px-9 py-4 rounded-lg shadow-lg shadow-[#C87A4B]/30 transition-all duration-200 hover:-translate-y-0.5"
          >
            Get a Quote <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </motion.div>
    </section>
  );
}
